import DropdownItemWrapper from '@app/components/ui/DropdownItemWrapper';
import { Dropdown, MenuProps, Tooltip } from 'antd';
import React, { memo, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MenuIcon } from './MenuIcon';
import { MenuItemProps } from './types';
import { getMenuItemClasses } from './utils';

type DropdownMenuItem = NonNullable<MenuProps['items']>[number];

/**
 * CollapsedMenuItem component for rendering menu items when sidebar is collapsed
 * Shows icons only with tooltips and dropdowns for items with children
 * Optimized with React.memo and useCallback for performance
 */
const CollapsedMenuItemComponent: React.FC<MenuItemProps> = ({
  item,
  isActive,
  hasChildren,
  hasActiveChild,
  index,
  depth,
}) => {
  // Memoized CSS classes based on current state
  const classes = useMemo(() => getMenuItemClasses(isActive, true), [isActive]);

  // Memoized container classes with conditional margin for first item at depth
  const containerClasses = useMemo(
    () => `
    flex justify-center items-center w-full 
    ${index === 0 && depth !== 0 ? 'mt-2' : ''}
  `,
    [index, depth]
  );

  // Memoized dropdown items
  const dropdownItems = useMemo(
    () =>
      hasChildren
        ? (item.children || []).map(function mapDropdownItem(child): DropdownMenuItem {
            return {
              key: child.key,
              label: (
                <DropdownItemWrapper>
                  <Link className="block w-full" to={String(child.key)}>
                    {child.label}
                  </Link>
                </DropdownItemWrapper>
              ),
              children: child.children?.map(mapDropdownItem),
            };
          })
        : [],
    [hasChildren, item.children]
  );

  // Render dropdown for items with children
  if (hasChildren) {
    return (
      <div className={containerClasses}>
        <Dropdown
          // open={true}
          trigger={['hover']}
          align={{ offset: [50, -30] }}
          menu={{
            items: dropdownItems,
          }}
          overlayClassName="z-50 w-56"
        >
          <span className={classes.container}>
            <MenuIcon icon={item.icon} isActive={isActive} hasActiveChild={hasActiveChild} className="text-2xl" />
          </span>
        </Dropdown>
      </div>
    );
  }

  // Render tooltip for items without children
  return (
    <div className={containerClasses}>
      <Tooltip title={item.label} placement="right">
        <Link className={classes.container} to={String(item.key)}>
          <MenuIcon icon={item.icon} isActive={isActive} className="text-xl" />
        </Link>
      </Tooltip>
    </div>
  );
};

// Memoized export with display name
CollapsedMenuItemComponent.displayName = 'CollapsedMenuItem';
export const CollapsedMenuItem = memo(CollapsedMenuItemComponent);
